import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowUp,
  ArrowDown,
  Eye,
  EyeOff,
  Trash2,
  X,
} from "lucide-react";
import { Link } from "react-router";
import { gql, useQuery } from "@apollo/client";
import { sectionRegistry, type SectionType } from "@sections/registry";
import { SectionErrorBoundary } from "@sections/SectionErrorBoundary";
import {
  type RenderContextValue,
  type BlogPostEntry,
  type FaqItemEntry,
  type BookingTypeEntry,
} from "@sections/RenderContext";
import { useBranding } from "../../../branding/BrandingProvider";
import { getFontCss, getFontUrl } from "../branding/fontStacks";
import type { EditorState } from "./useEditorState";
import type { PageForEdit } from "../pages/types";
import { useActiveTenant } from "../../../auth/AuthProvider";
import { InsertRail } from "./InsertRail";
import { InlineEditOverlay } from "./InlineEditOverlay";
import { useCanvasZoom } from "./useCanvasZoom";
import { DEFAULT_CANVAS_NAV, type CanvasNavItem } from "./canvasDefaults";
import { type AnnouncementConfig, readableText } from "../navigation/NavPreview";
import { onColor } from "@sections/colorUtils";
import {
  chromeContainerStyle,
  normalizeChromeWidth,
  type ChromeWidth,
} from "@sections/chromeWidth";
import { publicSiteBase } from "../../../hooks/publicSiteUrl";
import { parseBlogBasePath } from "../blog-edit/parseBlogBasePath";
import "@sections/richtext.css";

export interface EditorCanvasHandle {
  scrollToSection: (sectionId: string) => void;
}

interface EditorCanvasProps {
  page: PageForEdit;
  editor: EditorState;
  onOpenLibrary: (atIndex: number) => void;
  /** When true, the canvas shows a read-only overlay (version preview mode). */
  readOnly?: boolean;
}

/**
 * Viewport breakpoints in pixels.
 */
const VP_WIDTHS = {
  desktop: 1280,
  tablet: 740,
  mobile: 396,
} as const;

/**
 * Tenant asset URLs may be site-relative (e.g. /logo-color-small.png lives in
 * the public site's static files). Inside the portal those would resolve
 * against the portal origin and 404 — prefix them with the tenant's public
 * site base (localhost:3000 in dev, the tenant domain in production).
 */
function resolveSiteAssetUrl(
  url: string | null | undefined,
  tenantDomain: string,
): string | null {
  if (!url) return null;
  return url.startsWith("/") ? `${publicSiteBase(tenantDomain)}${url}` : url;
}

const CANVAS_TENANT_THEME = gql`
  query CanvasTenantTheme {
    currentTenant {
      id
      theme
    }
  }
`;

/**
 * Build inline style object with --tenant-* CSS custom properties
 * so section components render with the tenant's palette.
 *
 * Mirrors the live site's theme composition exactly: the tenant `theme`
 * JSON supplies the full palette (including accent/surface/text/textMuted
 * and fonts, which the branding table doesn't hold), and branding-table
 * colors overlay their seven slots on top — same as the public API's
 * _compose_theme_with_branding + the frontend's ThemeStyle.
 */
function useTenantVars(): React.CSSProperties {
  const { branding } = useBranding();
  const { data } = useQuery<{
    currentTenant: { theme: string | null } | null;
  }>(CANVAS_TENANT_THEME, { fetchPolicy: "cache-first" });

  return useMemo(() => {
    let theme: {
      colors?: Record<string, string>;
      typography?: Record<string, string>;
    } = {};
    try {
      theme = data?.currentTenant?.theme
        ? JSON.parse(data.currentTenant.theme)
        : {};
    } catch {
      theme = {};
    }
    const colors = theme.colors ?? {};
    const typography = theme.typography ?? {};

    const vars: Record<string, string> = {};
    // Set BOTH the --tenant-* slot and the Tailwind --color-*/--font-* token.
    // The @theme mappings (--color-surface: var(--tenant-surface, ...)) are
    // substituted at :root, where the tenant vars don't exist in the portal —
    // so container-level --tenant-* alone never reaches bg-surface etc.
    // Emitting the resolved token here shadows the :root fallback correctly.
    const set = (
      tenantVar: string,
      themeToken: string | null,
      value: string | null | undefined,
    ) => {
      if (!value) return;
      vars[tenantVar] = value;
      if (themeToken) vars[themeToken] = value;
    };
    // Branding-table colors win over theme JSON (same overlay as the live
    // site), and the derived-slot fallback chains mirror theme.css:
    // tertiary → accent, primaryBg → background, secondaryBg → surface.
    const background = branding?.backgroundColor ?? colors.background;
    set("--tenant-primary", "--color-primary", branding?.primaryColor ?? colors.primary);
    set("--tenant-secondary", "--color-secondary", branding?.secondaryColor ?? colors.secondary);
    set("--tenant-background", "--color-background", background);
    set("--tenant-tertiary", "--color-tertiary", branding?.tertiaryColor ?? colors.tertiary ?? colors.accent);
    set("--tenant-primary-bg", "--color-primary-bg", branding?.primaryBgColor ?? colors.primaryBg ?? background);
    set("--tenant-secondary-bg", "--color-secondary-bg", branding?.secondaryBgColor ?? colors.secondaryBg ?? colors.surface);
    set("--tenant-tertiary-bg", "--color-tertiary-bg", branding?.tertiaryBgColor ?? colors.tertiaryBg);
    // Theme-JSON-only slots (not editable via branding)
    set("--tenant-accent", "--color-accent", colors.accent);
    set("--tenant-surface", "--color-surface", colors.surface);
    set("--tenant-text", "--color-text", colors.text);
    set("--tenant-text-muted", "--color-text-muted", colors.textMuted);
    // Fonts: branding font keys overlay theme JSON typography (same as
    // the public API's _compose_theme_with_branding). Look up the CSS
    // font-family string from the font stack enum.
    const headingFontCss = getFontCss(branding?.headingFont) ?? typography.headingFont;
    const bodyFontCss = getFontCss(branding?.bodyFont) ?? typography.bodyFont;
    set("--tenant-heading-font", "--font-heading", headingFontCss);
    set("--tenant-body-font", "--font-body", bodyFontCss);

    // Luminance-derived text-contrast vars — mirrors ThemeStyle in
    // root.tsx so the canvas WYSIWYG matches the live site.
    const resolvedPrimary = branding?.primaryColor ?? colors.primary;
    const resolvedSecondary = branding?.secondaryColor ?? colors.secondary;
    const resolvedTertiary = branding?.tertiaryColor ?? colors.tertiary ?? colors.accent;
    const resolvedPrimaryBg = branding?.primaryBgColor ?? colors.primaryBg ?? background;
    const resolvedSecondaryBg = branding?.secondaryBgColor ?? colors.secondaryBg ?? colors.surface;
    const resolvedTertiaryBg = branding?.tertiaryBgColor ?? colors.tertiaryBg;
    if (resolvedPrimary) set("--tenant-on-primary", "--color-on-primary", onColor(resolvedPrimary));
    if (resolvedSecondary) set("--tenant-on-secondary", "--color-on-secondary", onColor(resolvedSecondary));
    if (resolvedTertiary) set("--tenant-on-tertiary", "--color-on-tertiary", onColor(resolvedTertiary));
    if (background) set("--tenant-on-brand", "--color-on-brand", onColor(background));
    if (resolvedPrimaryBg) set("--tenant-on-primary-bg", "--color-on-primary-bg", onColor(resolvedPrimaryBg));
    if (resolvedSecondaryBg) set("--tenant-on-secondary-bg", "--color-on-secondary-bg", onColor(resolvedSecondaryBg));
    if (resolvedTertiaryBg) set("--tenant-on-tertiary-bg", "--color-on-tertiary-bg", onColor(resolvedTertiaryBg));

    return vars as React.CSSProperties;
  }, [branding, data]);
}

/**
 * Inject Google Fonts <link> tags into the document head when the
 * tenant's branding specifies web fonts. This ensures the canvas
 * renders the selected fonts without an iframe or shadow DOM.
 */
function useCanvasFontLinks() {
  const { branding } = useBranding();
  useEffect(() => {
    const urls = new Set<string>();
    const hUrl = getFontUrl(branding?.headingFont);
    const bUrl = getFontUrl(branding?.bodyFont);
    if (hUrl) urls.add(hUrl);
    if (bUrl) urls.add(bUrl);

    const links: HTMLLinkElement[] = [];
    for (const url of urls) {
      // Skip if already loaded
      if (document.querySelector(`link[href="${url}"]`)) continue;
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = url;
      document.head.appendChild(link);
      links.push(link);
    }
    return () => {
      for (const link of links) link.remove();
    };
  }, [branding?.headingFont, branding?.bodyFont]);
}

// ── Announcement bar for canvas ─────────────────────────────────────

const CANVAS_ANNOUNCEMENT = gql`
  query CanvasAnnouncement {
    currentTenant {
      id
      announcementConfig
      chromeConfigDraft
      chromeConfigPublished
    }
  }
`;

/**
 * Pick a chrome zone's raw JSON for the canvas preview: the CMS chrome
 * (draft first, then published — matching what the editor is working on)
 * with fallback to the frozen legacy column for tenants that have never
 * saved CMS chrome. Zones inside chrome_config_* are stored in the same
 * shape as the legacy columns, so the existing parsers apply to both.
 */
function resolveChromeZone(
  tenant:
    | {
        chromeConfigDraft?: string | null;
        chromeConfigPublished?: string | null;
      }
    | null
    | undefined,
  zone: "nav" | "footer" | "announcement",
  legacy: string | null | undefined,
): string | null {
  for (const raw of [tenant?.chromeConfigDraft, tenant?.chromeConfigPublished]) {
    if (!raw) continue;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && parsed[zone]) {
        return JSON.stringify(parsed[zone]);
      }
    } catch {
      // malformed — try the next source
    }
  }
  return legacy ?? null;
}

function parseAnnouncementConfig(
  raw: string | null | undefined,
): AnnouncementConfig | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed.enabled || !parsed.message?.trim()) return null;
    return parsed as AnnouncementConfig;
  } catch {
    return null;
  }
}

function CanvasAnnouncementBar({ primaryColor }: { primaryColor: string }) {
  const { data } = useQuery<{
    currentTenant: {
      announcementConfig: string | null;
      chromeConfigDraft: string | null;
      chromeConfigPublished: string | null;
    } | null;
  }>(CANVAS_ANNOUNCEMENT, { fetchPolicy: "cache-first" });

  const config = useMemo(
    () =>
      parseAnnouncementConfig(
        resolveChromeZone(
          data?.currentTenant,
          "announcement",
          data?.currentTenant?.announcementConfig,
        ),
      ),
    [data],
  );

  if (!config) return null;

  const styleColors: Record<string, { bg: string; text: string }> = {
    primary: { bg: primaryColor, text: readableText(primaryColor) },
    dark: { bg: "#1a1a1a", text: "#ffffff" },
    accent: { bg: "var(--tenant-tertiary, #1e3a5f)", text: "#ffffff" },
  };
  const colors = styleColors[config.style] ?? styleColors.primary;

  return (
    <div
      className="flex items-center justify-center gap-3 px-4 py-2"
      style={{ backgroundColor: colors.bg, color: colors.text }}
    >
      <span className="text-[13px] font-medium text-center flex-1">
        {config.message}
      </span>
      {config.linkUrl && config.linkLabel && (
        <span
          className="flex-none rounded px-2.5 py-1 text-[11px] font-bold"
          style={{
            backgroundColor: "rgba(255,255,255,0.2)",
            color: colors.text,
          }}
        >
          {config.linkLabel}
        </span>
      )}
      {config.dismissible && (
        <X size={14} strokeWidth={2.5} className="flex-none opacity-60" />
      )}
    </div>
  );
}

// ── Canvas chrome (header + footer preview) ────────────────────────

const CANVAS_CHROME = gql`
  query CanvasChrome {
    currentTenant {
      id
      name
      chromeConfigDraft
      chromeConfigPublished
      navConfig
      logoUrl
      brandName
      footerLogoUrl
      footerConfig
      socialLinks
      phone
      email
      addresses
      legalEntityName
      legalDisclaimers
    }
  }
`;

interface CanvasChromeData {
  currentTenant: {
    id: string;
    name: string;
    chromeConfigDraft: string | null;
    chromeConfigPublished: string | null;
    navConfig: string | null;
    logoUrl: string | null;
    brandName: string | null;
    footerLogoUrl: string | null;
    footerConfig: string | null;
    socialLinks: string | null;
    phone: string | null;
    email: string | null;
    addresses: string | null;
    legalEntityName: string | null;
    legalDisclaimers: string | null;
  } | null;
}

function parseCanvasNavConfig(
  raw: string | null | undefined,
): CanvasNavItem[] {
  if (!raw) return DEFAULT_CANVAS_NAV;
  try {
    const parsed = JSON.parse(raw);
    const items = (parsed.items ?? []).map(
      (it: Record<string, unknown>, i: number) => ({
        key: (it.key as string) ?? `item-${i}`,
        label: (it.label as string) ?? "",
        primary: Boolean(it.primary),
      }),
    );
    return items.length > 0 ? items : DEFAULT_CANVAS_NAV;
  } catch {
    return DEFAULT_CANVAS_NAV;
  }
}

type CanvasFooterLayout =
  | "columns-4"
  | "columns-3"
  | "columns-2"
  | "centered"
  | "minimal";

interface CanvasFooterConfig {
  layout: CanvasFooterLayout;
  width: ChromeWidth;
  showQuickLinks: boolean;
  showContactInfo: boolean;
  showSocialLinks: boolean;
  showLegalInfo: boolean;
  quickLinksTitle: string;
  contactTitle: string;
  socialTitle: string;
  legalTitle: string;
  quickLinks: { label: string; key: string }[];
  sectionOrder: string[];
}

function parseCanvasFooterConfig(
  raw: string | null | undefined,
): CanvasFooterConfig {
  const defaults: CanvasFooterConfig = {
    layout: "columns-4",
    width: "contained",
    showQuickLinks: true,
    showContactInfo: true,
    showSocialLinks: true,
    showLegalInfo: true,
    quickLinksTitle: "Quick Links",
    contactTitle: "Contact",
    socialTitle: "Follow Us",
    legalTitle: "Legal",
    quickLinks: [],
    sectionOrder: ["social", "quickLinks", "contact", "legal"],
  };
  if (!raw) return defaults;
  try {
    const parsed = JSON.parse(raw);
    const flat = parsed.options ? { ...parsed, ...parsed.options } : parsed;
    return {
      layout: flat.layout ?? "columns-4",
      width: normalizeChromeWidth(flat.width),
      showQuickLinks: flat.showQuickLinks ?? true,
      showContactInfo: flat.showContactInfo ?? true,
      showSocialLinks: flat.showSocialLinks ?? true,
      showLegalInfo: flat.showLegalInfo ?? true,
      quickLinksTitle: flat.quickLinksTitle ?? "Quick Links",
      contactTitle: flat.contactTitle ?? "Contact",
      socialTitle: flat.socialTitle ?? "Follow Us",
      legalTitle: flat.legalTitle ?? "Legal",
      quickLinks: Array.isArray(flat.quickLinks)
        ? flat.quickLinks.map((l: { label?: string; key?: string }) => ({
            label: l.label ?? "Link",
            key: l.key ?? crypto.randomUUID(),
          }))
        : [],
      sectionOrder: Array.isArray(flat.sectionOrder)
        ? flat.sectionOrder
        : ["social", "quickLinks", "contact", "legal"],
    };
  } catch {
    return defaults;
  }
}

function parseCanvasSocialCount(raw: string | null | undefined): number {
  if (!raw) return 0;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
      return Object.values(parsed).filter(
        (v) => typeof v === "string" && (v as string).trim(),
      ).length;
    }
    return 0;
  } catch {
    return 0;
  }
}

function parseCanvasAddressLine(raw: string | null | undefined): string | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    const first = parsed[0] as { city?: string; state?: string };
    const parts = [first.city, first.state].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
  } catch {
    return null;
  }
}

/**
 * Read-only header preview for the canvas (announcement bar + nav bar).
 * Mirrors the live site structure: AnnouncementBar above the navbar.
 */
function CanvasHeaderPreview({
  primaryColor,
  readOnly,
}: {
  primaryColor: string;
  readOnly: boolean;
}) {
  const { data } = useQuery<CanvasChromeData>(CANVAS_CHROME, {
    fetchPolicy: "cache-first",
  });
  const tenant = useActiveTenant();

  const navRaw = useMemo(
    () =>
      resolveChromeZone(
        data?.currentTenant,
        "nav",
        data?.currentTenant?.navConfig,
      ),
    [data],
  );

  const navItems = useMemo(() => parseCanvasNavConfig(navRaw), [navRaw]);

  // Mirror the live site's Navbar: sticky when headerBehavior says so
  // (with the same backward-compat fallback to the deprecated `fixed`
  // boolean), and honor the chrome's CTA color override.
  const { isSticky, ctaColor, width } = useMemo(() => {
    const base = {
      isSticky: false,
      ctaColor: null as string | null,
      width: "contained" as ChromeWidth,
    };
    if (!navRaw) return base;
    try {
      const parsed = JSON.parse(navRaw);
      return {
        isSticky:
          parsed.headerBehavior === "sticky" ||
          parsed.headerBehavior === "fixed" ||
          (parsed.headerBehavior === undefined && parsed.fixed === true),
        ctaColor: typeof parsed.ctaColor === "string" ? parsed.ctaColor : null,
        width: normalizeChromeWidth(parsed.width),
      };
    } catch {
      return base;
    }
  }, [navRaw]);

  const [logoFailed, setLogoFailed] = useState(false);
  const logoUrl = resolveSiteAssetUrl(data?.currentTenant?.logoUrl, tenant.domain);
  const displayName = data?.currentTenant?.brandName || tenant.name;
  const regularItems = navItems.filter((i) => !i.primary);
  const primaryItems = navItems.filter((i) => i.primary);

  const editAffordance = !readOnly && (
    <div className="absolute inset-0 z-[3] flex items-center justify-center opacity-0 group-hover/header:opacity-100 transition-opacity duration-150 pointer-events-none">
      <div className="absolute inset-0 bg-black/[0.04]" />
      <Link
        to="/website/chrome"
        className="relative pointer-events-auto"
        style={{
          zoom: "var(--canvas-unzoom, 1)",
          textDecoration: "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="bg-ink text-white text-[11.5px] font-medium px-3.5 py-1.5 rounded-full shadow-lg whitespace-nowrap font-sans">
          Edit in Header &amp; Footer
        </span>
      </Link>
    </div>
  );

  // The announcement bar and nav are siblings (not one wrapper) so the nav
  // can be position:sticky — a sticky element can't travel outside its
  // parent, so its parent must be the full-height canvas, like the live
  // site where AnnouncementBar scrolls away and only the navbar pins.
  return (
    <>
      <div className="relative group/header">
        <CanvasAnnouncementBar primaryColor={primaryColor} />
        {editAffordance}
      </div>

      {/* Nav bar (sticks within the canvas scroll pane like the live site) */}
      <div className={`relative group/header${isSticky ? " sticky top-0 z-[5]" : ""}`}>
      <div
        className="bg-white"
        style={{ borderBottom: "1px solid #e5e7eb" }}
      >
      <div
        className="flex items-center px-8 py-3"
        style={{ ...chromeContainerStyle(width), marginInline: "auto" }}
      >
        {/* Logo / tenant name */}
        {logoUrl && !logoFailed ? (
          <img
            src={logoUrl}
            alt=""
            className="h-8 w-auto object-contain"
            style={{ maxWidth: 180 }}
            onError={() => setLogoFailed(true)}
          />
        ) : (
          <span
            className="text-[18px] font-bold"
            style={{ color: "var(--tenant-text, #2c3e50)" }}
          >
            {displayName}
          </span>
        )}

        {/* Nav links */}
        <div className="flex items-center gap-1 ml-auto">
          {regularItems.map((item) => (
            <span
              key={item.key}
              className="px-3 py-1.5 text-[14px] font-medium"
              style={{ color: "#4b5563" }}
            >
              {item.label || "Link"}
            </span>
          ))}
          {primaryItems.map((item) => (
            <span
              key={item.key}
              className="rounded-md px-4 py-2 text-[14px] font-semibold"
              style={{
                backgroundColor: ctaColor ?? primaryColor,
                color: readableText(ctaColor ?? primaryColor),
              }}
            >
              {item.label || "Link"}
            </span>
          ))}
        </div>
      </div>
      </div>

      {/* Hover affordance (edit-mode only) */}
      {editAffordance}
      </div>
    </>
  );
}

/**
 * Read-only footer preview for the canvas.
 * Renders a representative footer matching the tenant's footer config
 * (layout, sections, colors) without the editor card wrapper.
 */
function CanvasFooterPreview({ readOnly }: { readOnly: boolean }) {
  const { data } = useQuery<CanvasChromeData>(CANVAS_CHROME, {
    fetchPolicy: "cache-first",
  });
  const tenant = useActiveTenant();
  const { branding } = useBranding();

  const config = useMemo(
    () =>
      parseCanvasFooterConfig(
        resolveChromeZone(
          data?.currentTenant,
          "footer",
          data?.currentTenant?.footerConfig,
        ),
      ),
    [data],
  );

  const [logoFailed, setLogoFailed] = useState(false);
  const logoUrl = resolveSiteAssetUrl(
    data?.currentTenant?.footerLogoUrl ?? data?.currentTenant?.logoUrl,
    tenant.domain,
  );
  const displayName = data?.currentTenant?.brandName || tenant.name;
  const legalEntity = data?.currentTenant?.legalEntityName ?? null;
  const legalDisclaimers = data?.currentTenant?.legalDisclaimers ?? null;
  const phone = data?.currentTenant?.phone ?? null;
  const email = data?.currentTenant?.email ?? null;
  const socialCount = useMemo(
    () => parseCanvasSocialCount(data?.currentTenant?.socialLinks),
    [data],
  );
  const addressLine = useMemo(
    () => parseCanvasAddressLine(data?.currentTenant?.addresses),
    [data],
  );

  // Match the live site's footer background: $footer-background in
  // _variables.scss equals $body-background-color, which maps to the
  // tenant's branding backgroundColor in the multi-tenant system.
  const footerBg = branding?.backgroundColor ?? "#196891";
  const footerText = readableText(footerBg);
  const footerMuted =
    footerText === "#ffffff"
      ? "rgba(255,255,255,0.6)"
      : "rgba(0,0,0,0.5)";

  // Resolve quick link labels
  const quickLabels =
    config.quickLinks.length > 0
      ? config.quickLinks.map((l) => l.label)
      : ["About", "Services", "Blog"];

  // Layout capacity
  const capacity =
    config.layout === "columns-4"
      ? 4
      : config.layout === "columns-3"
        ? 3
        : config.layout === "columns-2"
          ? 2
          : config.layout === "centered"
            ? Infinity
            : 0;

  const enabledSections = config.sectionOrder
    .filter((key) => {
      if (key === "social")
        return config.showSocialLinks && socialCount > 0;
      if (key === "quickLinks") return config.showQuickLinks;
      if (key === "contact")
        return (
          config.showContactInfo && !!(phone || email || addressLine)
        );
      // Live LegalBlock renders the disclaimer only -- the entity name goes in
      // the copyright bar -- so an entity with no disclaimer is an empty column.
      if (key === "legal") return config.showLegalInfo && !!legalDisclaimers;
      return false;
    })
    .slice(0, capacity);

  const renderSection = (key: string) => {
    switch (key) {
      case "social":
        return (
          <div>
            <div
              className="text-[13px] font-semibold mb-2"
              style={{ color: footerText }}
            >
              {config.socialTitle}
            </div>
            <div className="flex gap-2">
              {Array.from({ length: Math.min(socialCount, 5) }).map(
                (_, i) => (
                  <span
                    key={i}
                    className="w-5 h-5 rounded-full"
                    style={{ backgroundColor: footerMuted }}
                  />
                ),
              )}
            </div>
          </div>
        );
      case "quickLinks":
        return (
          <div>
            <div
              className="text-[13px] font-semibold mb-2"
              style={{ color: footerText }}
            >
              {config.quickLinksTitle}
            </div>
            <div className="space-y-1">
              {quickLabels.slice(0, 5).map((label) => (
                <div
                  key={label}
                  className="text-[12px]"
                  style={{ color: footerMuted }}
                >
                  {label}
                </div>
              ))}
            </div>
          </div>
        );
      case "contact":
        return (
          <div>
            <div
              className="text-[13px] font-semibold mb-2"
              style={{ color: footerText }}
            >
              {config.contactTitle}
            </div>
            <div
              className="space-y-0.5 text-[12px]"
              style={{ color: footerMuted }}
            >
              {phone && <div>{phone}</div>}
              {email && <div>{email}</div>}
              {addressLine && <div>{addressLine}</div>}
            </div>
          </div>
        );
      case "legal":
        return (
          <div>
            <div
              className="text-[13px] font-semibold mb-2"
              style={{ color: footerText }}
            >
              {config.legalTitle}
            </div>
            <div
              className="text-[12px]"
              style={{ color: footerMuted }}
            >
              {legalDisclaimers && (
                <div className="mt-0.5">
                  {legalDisclaimers.length > 120
                    ? legalDisclaimers.slice(0, 120) + "..."
                    : legalDisclaimers}
                </div>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderFooterContent = () => {
    if (config.layout === "minimal") {
      return (
        <div className="flex items-center justify-between">
          <span
            className="text-[14px] font-semibold"
            style={{ color: footerText }}
          >
            {tenant.name}
          </span>
          {config.showSocialLinks && socialCount > 0 && (
            <div className="flex gap-2">
              {Array.from({ length: Math.min(socialCount, 5) }).map(
                (_, i) => (
                  <span
                    key={i}
                    className="w-5 h-5 rounded-full"
                    style={{ backgroundColor: footerMuted }}
                  />
                ),
              )}
            </div>
          )}
        </div>
      );
    }

    if (config.layout === "centered") {
      return (
        <div className="text-center space-y-3">
          {logoUrl && !logoFailed ? (
            <img
              src={logoUrl}
              alt=""
              className="mx-auto h-8 w-auto object-contain"
              style={{ maxWidth: 160 }}
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <div
              className="text-[16px] font-semibold"
              style={{ color: footerText }}
            >
              {displayName}
            </div>
          )}
          {enabledSections.map((key) => (
            <div key={key}>{renderSection(key)}</div>
          ))}
        </div>
      );
    }

    // Column layouts (columns-4, columns-3, columns-2)
    const cols =
      config.layout === "columns-4"
        ? 4
        : config.layout === "columns-3"
          ? 3
          : 2;

    const firstSection = enabledSections[0] ?? null;
    const restSections = enabledSections.slice(1);

    return (
      <div
        className="grid gap-8"
        style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
      >
        {/* Brand column + first section */}
        <div>
          {logoUrl && !logoFailed ? (
            <img
              src={logoUrl}
              alt=""
              className="h-7 w-auto object-contain mb-3"
              style={{ maxWidth: 140 }}
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <div
              className="text-[14px] font-semibold mb-3"
              style={{ color: footerText }}
            >
              {displayName}
            </div>
          )}
          {firstSection && renderSection(firstSection)}
        </div>

        {/* Remaining section columns */}
        {restSections.map((key) => (
          <div key={key}>{renderSection(key)}</div>
        ))}
      </div>
    );
  };

  return (
    <div className="relative group/footer">
      {/* Footer content */}
      <div className="py-8" style={{ backgroundColor: footerBg }}>
        <div
          className="px-10"
          style={{ ...chromeContainerStyle(config.width), marginInline: "auto" }}
        >
          {renderFooterContent()}
        </div>
      </div>

      {/* System footer bar */}
      <div
        className="py-3 text-[12px]"
        style={{
          backgroundColor: footerBg,
          borderTop:
            footerText === "#ffffff"
              ? "1px solid rgba(255,255,255,0.12)"
              : "1px solid rgba(0,0,0,0.1)",
          color: footerMuted,
        }}
      >
        <div
          className="px-10 flex items-center justify-between"
          style={{ ...chromeContainerStyle(config.width), marginInline: "auto" }}
        >
          <span>
            &copy; {new Date().getFullYear()}{" "}
            {legalEntity || tenant.name}. All rights reserved.
          </span>
          <span className="flex gap-3">
            <span>Terms of Use</span>
            <span>Privacy Policy</span>
          </span>
        </div>
      </div>

      {/* Hover affordance (edit-mode only) */}
      {!readOnly && (
        <div className="absolute inset-0 z-[3] flex items-center justify-center opacity-0 group-hover/footer:opacity-100 transition-opacity duration-150 pointer-events-none">
          <div className="absolute inset-0 bg-black/[0.04]" />
          <Link
            to="/website/chrome"
            className="relative pointer-events-auto"
            style={{
              zoom: "var(--canvas-unzoom, 1)",
              textDecoration: "none",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <span className="bg-ink text-white text-[11.5px] font-medium px-3.5 py-1.5 rounded-full shadow-lg whitespace-nowrap font-sans">
              Edit in Header &amp; Footer
            </span>
          </Link>
        </div>
      )}
    </div>
  );
}

// ── Canvas preview data queries ──────────────────────────────────────

const CANVAS_FEATURE_CONFIG = gql`
  query CanvasFeatureConfig {
    currentTenant {
      id
      featureConfig
    }
  }
`;

const CANVAS_BLOG_POSTS = gql`
  query CanvasBlogPosts {
    allBlogPosts(
      page: 0
      perPage: 12
      sortField: "publishAt"
      sortOrder: "DESC"
      filter: { status: "published" }
    ) {
      id
      title
      author
      summary
      publishAt
      featuredImageUrl
    }
  }
`;

const CANVAS_FAQS = gql`
  query CanvasFaqs {
    allFaqs(
      page: 0
      perPage: 50
      sortField: "sortOrder"
      sortOrder: "ASC"
      filter: { status: "on_faq_page" }
    ) {
      id
      question
      answer
      sortOrder
    }
  }
`;

const CANVAS_BOOKING_TYPES = gql`
  query CanvasBookingTypes {
    allBookingTypes(
      page: 0
      perPage: 50
      sortField: "sortOrder"
      sortOrder: "ASC"
      filter: { isActive: { eq: true } }
    ) {
      id
      name
      description
      slug
      sortOrder
      minCustomers
      maxCustomers
      customerCountType
      pricingPlans {
        pricingPlanId
        minCustomerCount
        maxCustomerCount
      }
    }
    allPricingPlans(
      page: 0
      perPage: 100
      sortField: "name"
      sortOrder: "ASC"
    ) {
      id
      name
      authAmount
      hourlyRate
    }
  }
`;

interface CanvasBlogData {
  allBlogPosts: Array<{
    id: string;
    title: string;
    author: string;
    summary: string | null;
    publishAt: string | null;
    featuredImageUrl: string | null;
  }>;
}

interface CanvasFaqData {
  allFaqs: Array<{
    id: string;
    question: string;
    answer: string;
    sortOrder: number | null;
  }>;
}

interface CanvasBookingData {
  allBookingTypes: Array<{
    id: string;
    name: string;
    description: string | null;
    slug: string | null;
    sortOrder: number | null;
    minCustomers: number;
    maxCustomers: number;
    customerCountType: string;
    pricingPlans: Array<{
      pricingPlanId: string;
      minCustomerCount: number;
      maxCustomerCount: number;
    }>;
  }>;
  allPricingPlans: Array<{
    id: string;
    name: string;
    authAmount: number;
    hourlyRate: number;
  }>;
}

/**
 * Fetch real tenant data for WYSIWYG preview in the canvas.
 * Blog posts, FAQs, and booking types populate the RenderContext so
 * BlogGrid, FAQ, and PricingTable sections show real content.
 */
export function useCanvasRenderContext(): RenderContextValue {
  const blogQuery = useQuery<CanvasBlogData>(CANVAS_BLOG_POSTS, {
    fetchPolicy: "cache-first",
  });
  const faqQuery = useQuery<CanvasFaqData>(CANVAS_FAQS, {
    fetchPolicy: "cache-first",
  });
  const bookingQuery = useQuery<CanvasBookingData>(CANVAS_BOOKING_TYPES, {
    fetchPolicy: "cache-first",
  });
  const featureQuery = useQuery<{
    currentTenant: { featureConfig: string | null } | null;
  }>(CANVAS_FEATURE_CONFIG, { fetchPolicy: "cache-first" });

  return useMemo(() => {
    // Blog posts
    const blogPosts: BlogPostEntry[] = (blogQuery.data?.allBlogPosts ?? []).map(
      (p) => ({
        id: p.id,
        title: p.title,
        slug: "", // slug not needed for canvas preview
        author: p.author,
        summary: p.summary ?? undefined,
        publishAt: p.publishAt ?? undefined,
        featuredImageUrl: p.featuredImageUrl ?? undefined,
      }),
    );

    // FAQs
    const faqItems: FaqItemEntry[] = (faqQuery.data?.allFaqs ?? []).map(
      (f) => ({
        id: f.id,
        question: f.question,
        answer: f.answer,
        sortOrder: f.sortOrder ?? undefined,
      }),
    );

    // Booking types — join with pricing plans to get hourlyRate
    const plansById = new Map(
      (bookingQuery.data?.allPricingPlans ?? []).map((p) => [p.id, p]),
    );
    const bookingTypes: BookingTypeEntry[] = (
      bookingQuery.data?.allBookingTypes ?? []
    ).map((bt) => {
      // Pick the first linked pricing plan (most booking types have one)
      const firstTier = bt.pricingPlans[0];
      const plan = firstTier ? plansById.get(firstTier.pricingPlanId) : undefined;
      return {
        id: bt.id,
        name: bt.name,
        description: bt.description ?? undefined,
        sortOrder: bt.sortOrder ?? undefined,
        slug: bt.slug ?? undefined,
        minCustomers: bt.minCustomers,
        maxCustomers: bt.maxCustomers,
        customerCountType: bt.customerCountType,
        pricingPlan: plan
          ? {
              name: plan.name,
              authAmount: plan.authAmount,
              hourlyRate: plan.hourlyRate,
            }
          : undefined,
      };
    });

    // Derive blogBasePath from tenant featureConfig
    const blogBasePath = parseBlogBasePath(
      featureQuery.data?.currentTenant?.featureConfig,
    );

    return { blogPosts, blogBasePath, faqItems, bookingTypes, isEditor: true };
  }, [blogQuery.data, faqQuery.data, bookingQuery.data, featureQuery.data]);
}

/**
 * Floating toolbar that appears at the top-right of the selected section.
 * Provides move, visibility, and delete controls.
 */
function SectionToolbar({
  editor,
  sectionId,
  isFirst,
  isLast,
  isHidden,
}: {
  editor: EditorState;
  sectionId: string;
  isFirst: boolean;
  isLast: boolean;
  isHidden: boolean;
}) {
  const btnBase =
    "flex items-center justify-center w-[30px] h-[28px] rounded-[7px] border-0 bg-transparent text-ink-3 transition-colors hover:bg-paper-2 hover:text-ink-2 disabled:opacity-30 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:text-ink-3";

  return (
    <div
      className="absolute top-2.5 right-2.5 z-[7] flex items-center gap-0.5 bg-paper-hi rounded-[9px] px-1 py-[3px] shadow-card pointer-events-auto"
      style={{ zoom: "var(--canvas-unzoom, 1)" }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        className={btnBase}
        title="Move up"
        disabled={isFirst}
        onClick={() => editor.moveSection(sectionId, "up")}
      >
        <ArrowUp size={14} strokeWidth={2} />
      </button>
      <button
        className={btnBase}
        title="Move down"
        disabled={isLast}
        onClick={() => editor.moveSection(sectionId, "down")}
      >
        <ArrowDown size={14} strokeWidth={2} />
      </button>

      {/* Divider */}
      <div className="w-px h-[18px] bg-ink-6 mx-0.5" />

      <button
        className={btnBase}
        title={isHidden ? "Show to visitors" : "Hide from visitors"}
        onClick={() => editor.toggleSectionVisibility(sectionId)}
      >
        {isHidden ? (
          <EyeOff size={14} strokeWidth={2} />
        ) : (
          <Eye size={14} strokeWidth={2} />
        )}
      </button>
      <button
        className={`${btnBase} hover:!bg-rust-s hover:!text-rust`}
        title="Delete section"
        onClick={() => editor.removeSection(sectionId)}
      >
        <Trash2 size={14} strokeWidth={2} />
      </button>
    </div>
  );
}

/** Resolve a dot-separated path to its value inside a nested object. */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc == null) return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}

/** Tracks which text prop is being inline-edited on the canvas. */
interface EditingProp {
  sectionId: string;
  propName: string;
  /** Reference to the DOM element being edited over. */
  targetEl: HTMLElement;
}

export const EditorCanvas = forwardRef<EditorCanvasHandle, EditorCanvasProps>(
  function EditorCanvas({ page, editor, onOpenLibrary, readOnly = false }, ref) {
    const canvasWrapRef = useRef<HTMLDivElement>(null);
    const tenantVars = useTenantVars();
    const tenant = useActiveTenant();
    useCanvasFontLinks();
    const [editingProp, setEditingProp] = useState<EditingProp | null>(null);
    const canvasZoom = useCanvasZoom(canvasWrapRef, VP_WIDTHS[editor.viewport]);

    const scrollToSection = useCallback((sectionId: string) => {
      const el = canvasWrapRef.current?.querySelector(
        `[data-section-id="${sectionId}"]`,
      );
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, []);

    useImperativeHandle(ref, () => ({ scrollToSection }), [scrollToSection]);

    // Prevent links and buttons inside sections from navigating/activating in
    // edit mode. Uses capture phase so it fires before React handlers and
    // doesn't stop propagation (section-selection + inline-edit still work).
    useEffect(() => {
      const el = canvasWrapRef.current;
      if (!el) return;
      function interceptClicks(e: MouseEvent) {
        const target = e.target as HTMLElement;
        // Only intercept inside rendered section content
        if (!target.closest("[data-section-id]")) return;
        // Don't intercept editor chrome (toolbar buttons, bubble menu, etc.)
        if (target.closest("[data-editor-chrome]")) return;
        const interactive = target.closest("a, button");
        if (interactive) {
          e.preventDefault();
        }
      }
      el.addEventListener("click", interceptClicks, { capture: true });
      return () =>
        el.removeEventListener("click", interceptClicks, { capture: true });
    }, []);

    // Track whether inline editing is active for undo coalescing.
    // We commit one undo checkpoint when the inline editor closes
    // instead of per-keystroke, keeping page-level undo clean.
    const wasInlineEditingRef = useRef(false);

    /** Close inline editor when clicking on canvas background or switching sections. */
    const closeInlineEditor = useCallback(() => {
      if (wasInlineEditingRef.current) {
        editor.commitUndoCheckpoint();
        wasInlineEditingRef.current = false;
      }
      setEditingProp(null);
    }, [editor]);

    // Clear inline editing when the selected section changes
    useEffect(() => {
      if (
        editingProp &&
        editingProp.sectionId !== editor.selectedSectionId
      ) {
        // Section changed while inline editing -- commit undo checkpoint
        if (wasInlineEditingRef.current) {
          editor.commitUndoCheckpoint();
          wasInlineEditingRef.current = false;
        }
        setEditingProp(null);
      }
    }, [editor, editor.selectedSectionId, editingProp]);

    const draftUrl = page.isHome
      ? `${tenant.domain}/`
      : `${tenant.domain}/${page.slug}`;

    return (
      <main
        ref={canvasWrapRef}
        // No padding-top here on purpose. Chrome insets a sticky element's
        // constraint rect by the scroll container's padding, so a padding-top
        // on the scrollport parks `sticky top-0` children that far down --
        // which showed up as a permanent gap above the pinned site header,
        // constant at every canvas zoom. The top spacing lives on the first
        // child instead.
        className="flex-1 overflow-y-auto px-[30px] pb-20"
        style={{ scrollBehavior: "smooth" }}
        onClick={(e) => {
          // Clicking the gray workspace background deselects. Section clicks
          // stop propagation; still guard against anything interactive
          // (insert rails, add-section button, editor chrome like the
          // TipTap BubbleMenu which portals outside the section wrapper)
          // bubbling up here.
          const target = e.target as HTMLElement;
          if (
            target.closest("[data-section-id]") ||
            target.closest("button") ||
            target.closest("[data-editor-chrome]")
          ) {
            return;
          }
          editor.selectSection(null);
        }}
      >
        {/* URL pill */}
        <div className="flex justify-center pt-6 pb-3">
          <span className="bg-paper border border-ink-6 rounded-full px-3.5 py-1 font-mono text-[11.5px] text-ink-5">
            {draftUrl}
            <span className="text-ink-3">
              {readOnly
                ? " — read-only preview"
                : " — draft preview"}
            </span>
          </span>
        </div>

        {/* Canvas container */}
        <div
          className="@container mx-auto bg-white rounded-[14px] shadow-lift overflow-clip transition-[width] duration-[350ms] ease-[cubic-bezier(0.3,0.9,0.3,1)] relative"
          style={{
            width: VP_WIDTHS[editor.viewport],
            zoom: canvasZoom,
            ["--canvas-unzoom" as string]: 1 / canvasZoom,
            ...tenantVars,
          }}
        >
            {/* Read-only preview banner (version history mode) */}
            {readOnly && (
              <div className="absolute top-[100px] inset-x-0 z-[10] flex justify-center pointer-events-none">
                <div
                  className="bg-ink text-white text-[11.5px] font-medium px-[14px] py-[5px] rounded-full opacity-[0.88] whitespace-nowrap shadow-lg"
                  style={{ zoom: "var(--canvas-unzoom, 1)" }}
                >
                  Read-only preview -- restoring loads this version into your draft
                </div>
              </div>
            )}

            {/* Header preview (announcement bar + nav bar) */}
            <CanvasHeaderPreview
              primaryColor={
                (tenantVars as Record<string, string>)["--tenant-primary"] ??
                "#2663C7"
              }
              readOnly={readOnly}
            />

            {editor.content.map((row, idx) => {
              const mod = sectionRegistry[row.type as SectionType];
              if (!mod) {
                return (
                  <div key={row.id}>
                    <InsertRail onClick={() => onOpenLibrary(idx)} />
                    <div className="py-6 text-center text-ink-4 text-[13px]">
                      Unknown section type: &ldquo;{row.type}&rdquo;
                    </div>
                  </div>
                );
              }

              const SectionComponent = mod.Component;
              const isSelected = editor.selectedSectionId === row.id;
              const isHidden = row.enabled === false;

              return (
                <div key={row.id}>
                  {/* Insert rail before this section (hidden in read-only mode) */}
                  {!readOnly && (
                    <InsertRail onClick={() => onOpenLibrary(idx)} />
                  )}

                  <div
                    data-section-id={row.id}
                    onClick={(e) => {
                      if (readOnly) return;
                      e.stopPropagation();
                      editor.selectSection(row.id ?? null);

                      const target = e.target as HTMLElement;

                      // Check for item-level focus (images in repeatable items)
                      const itemPropEl = target.closest<HTMLElement>(
                        "[data-item-prop]",
                      );
                      if (itemPropEl) {
                        const itemPropValue =
                          itemPropEl.getAttribute("data-item-prop");
                        if (itemPropValue) {
                          const lastDot = itemPropValue.lastIndexOf(".");
                          if (lastDot !== -1) {
                            const arrayProp = itemPropValue.substring(
                              0,
                              lastDot,
                            );
                            const index = parseInt(
                              itemPropValue.substring(lastDot + 1),
                              10,
                            );
                            if (!isNaN(index)) {
                              editor.setFocusItemRequest({
                                arrayProp,
                                index,
                              });
                            }
                          }
                        }
                        if (editingProp) {
                          closeInlineEditor();
                        }
                        return;
                      }

                      // Detect if the click landed on a [data-prop] element
                      const propEl = target.closest<HTMLElement>(
                        "[data-prop]",
                      );
                      if (propEl && row.id && isSelected) {
                        const propName = propEl.getAttribute("data-prop");
                        if (propName) {
                          setEditingProp({
                            sectionId: row.id,
                            propName,
                            targetEl: propEl,
                          });
                          return;
                        }
                      }

                      // Clicking outside a data-prop closes the inline editor
                      if (editingProp) {
                        closeInlineEditor();
                      }
                    }}
                    className={`relative ${readOnly ? "" : "group cursor-pointer"}`}
                  >
                    {/* Selection ring — an overlay above the section content.
                        An inset ring on the wrapper itself paints beneath the
                        section's own background fill, making it invisible.
                        Hidden in read-only mode. */}
                    {!readOnly && (
                      <div
                        aria-hidden
                        className={`absolute inset-0 z-[5] pointer-events-none ring-2 ring-inset transition-shadow duration-150 ${
                          isSelected
                            ? "ring-tide"
                            : "ring-transparent group-hover:ring-tide/45"
                        }`}
                      />
                    )}

                    {/* Section type tag (shows on hover and selection, hidden in read-only) */}
                    {!readOnly && (
                      <span
                        style={{ zoom: "var(--canvas-unzoom, 1)" }}
                        className={`absolute top-2.5 left-2.5 z-[6] text-[10px] font-bold tracking-[0.07em] uppercase bg-tide text-white px-2.5 py-[3px] rounded-md pointer-events-none transition-all duration-150 font-sans ${
                          isSelected
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0"
                        }`}
                      >
                        {row.type}
                      </span>
                    )}

                    {/* Floating toolbar (selected only, hidden in read-only) */}
                    {!readOnly && isSelected && row.id && (
                      <SectionToolbar
                        editor={editor}
                        sectionId={row.id}
                        isFirst={idx === 0}
                        isLast={idx === editor.content.length - 1}
                        isHidden={isHidden}
                      />
                    )}

                    {/* Hidden overlay */}
                    {isHidden && (
                      <div className="absolute inset-0 z-[6] grid place-items-center pointer-events-none">
                        <div
                          className="text-[12px] font-semibold text-ink-3 bg-paper-hi border border-ink-6 px-3.5 py-1.5 rounded-full shadow-card font-sans"
                          style={{ zoom: "var(--canvas-unzoom, 1)" }}
                        >
                          Hidden &mdash; visitors don&apos;t see this
                        </div>
                      </div>
                    )}

                    {/* Section content */}
                    <div
                      className={
                        isHidden ? "opacity-35 saturate-[0.4]" : undefined
                      }
                    >
                      <SectionErrorBoundary
                        key={row.id + JSON.stringify(row.props)}
                      >
                        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                        <SectionComponent {...(row.props as any)} />
                      </SectionErrorBoundary>
                    </div>

                    {/* Inline text editing overlay (hidden in read-only) */}
                    {!readOnly &&
                      isSelected &&
                      editingProp &&
                      editingProp.sectionId === row.id && (
                        <InlineEditOverlay
                          key={editingProp.propName}
                          targetEl={editingProp.targetEl}
                          propName={editingProp.propName}
                          value={
                            (editingProp.propName.includes(".")
                              ? (getNestedValue(row.props, editingProp.propName) as string)
                              : (row.props[editingProp.propName] as string)) ?? ""
                          }
                          onChange={(html) => {
                            if (row.id) {
                              // Use silent updates so per-keystroke edits
                              // don't flood the page-level undo stack.
                              wasInlineEditingRef.current = true;
                              if (editingProp.propName.includes(".")) {
                                editor.updateSectionPropSilent(
                                  row.id,
                                  editingProp.propName,
                                  html,
                                );
                              } else {
                                editor.updateSectionSilent(row.id, {
                                  [editingProp.propName]: html,
                                });
                              }
                            }
                          }}
                          onClose={closeInlineEditor}
                        />
                      )}
                  </div>
                </div>
              );
            })}

            {/* Insert rail after the last section (hidden in read-only) */}
            {!readOnly && editor.content.length > 0 && (
              <InsertRail
                onClick={() => onOpenLibrary(editor.content.length)}
              />
            )}

          {/* Empty state (hidden in read-only) */}
          {!readOnly && editor.content.length === 0 && (
            <button
              onClick={() => onOpenLibrary(0)}
              className="w-full py-20 text-center text-ink-4 text-[14px] hover:bg-tide-ss transition-colors rounded-[14px] border-0 bg-transparent cursor-pointer"
            >
              <p className="mb-2 font-medium">No sections yet</p>
              <p className="text-[13px] text-ink-5">
                Click here or &ldquo;Add section&rdquo; in the outline to get
                started.
              </p>
            </button>
          )}

            {/* Footer preview */}
            <CanvasFooterPreview readOnly={readOnly} />
        </div>
      </main>
    );
  },
);
